// My books › Library copies (Phase 6.4 Part 2, step 1; agreed with Kristi Oct 31, laptop mockup
// in chat): which section each book goes in, Holds to place, Coming up and the header line.
// No database calls here, so the rules are tested on their own (tests/logic/my-books-library.test.ts).

import type { ShelfBook } from '../shelves/model';
import type { Shelves } from '../shelves/load';
import { addDays, dayText } from '../book/model';
import { bookState, lineWords, loanState, needsParts, needsYou, quietedHold, stateRank, stateWords, whenWords, type LoanState, type SummaryPart } from '../lib/libraryStates';

/** One library hold or loan still going (on hold, ready or borrowed). */
export interface LoanFact {
  id: string;
  copyId: string;
  bookId: string;
  /** The copy's format as stored (kindle, libby…) and whether it's an audiobook. */
  format: string;
  listen: boolean;
  library: string | null;
  libbyCode: string | null;
  step: 'on_hold' | 'ready' | 'borrowed';
  holdPlacedOn: string | null;
  expectedOn: string | null;
  borrowBy: string | null;
  borrowedOn: string | null;
  dueOn: string | null;
  placeInLine: number | null;
  libraryCopies: number | null;
  /** The library's settings: Check Libby this many days before the expected date; Renew from. */
  checkDays: number;
  renewDays: number;
  /** Checked, not ready yet: quiet through this day (0051). */
  quietUntil?: string | null;
}

/** One of your copies, enough to tell whether you own the book. */
export interface CopyFact {
  bookId: string;
  how: string;
  state: string;
}

/**
 * Why a book is in its section: its library state, from the one list (consistency pass R2, Kristi
 * Nov 2; lib/libraryStates.ts): loan ended, ready, due soon, check Libby, not started (these need
 * you), then on loan, on hold.
 */
export type Why = LoanState;

export interface LibRow {
  book: ShelfBook;
  why: Why;
  /** On Currently Reading (the row says "Reading" first). */
  reading: boolean;
  /** Every hold and loan the book has, most urgent first (John of John: Kindle and Libby audio). */
  loans: LoanFact[];
}

export interface LibrarySections {
  /** Check & update (L4): every book that needs you (loan ended, ready, due soon, check Libby, not started). */
  check: LibRow[];
  /** On loan: books being read, soonest due first. */
  loan: LibRow[];
  /** On hold: the quiet holds, soonest expected first. */
  hold: LibRow[];
  /**
   * Holds you checked and quieted (Not ready yet ▾, Kristi Nov 2): grey at the foot of Check status
   * until the quiet ends, soonest back first. They don't count toward the tab's dot.
   */
  quiet: LibRow[];
}

/** A copy's name in a row ("Kindle", "Libby audio"). */
export function loanFormat(l: Pick<LoanFact, 'format' | 'listen'>): string {
  if (l.format === 'kindle') return 'Kindle';
  if (l.format === 'libby') return l.listen ? 'Libby audio' : 'Libby ebook';
  if (l.format === 'hoopla') return 'Hoopla';
  return l.format.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** One loan's state (the one list, so Shelves and Library copies agree). */
export function whyOf(l: LoanFact, reading: boolean, today: string): Why {
  return loanState(l, reading, today)!;
}

/** The date a loan sorts by in its section. */
const dateOf = (l: LoanFact): string => (l.step === 'borrowed' ? l.dueOn : l.step === 'ready' ? l.borrowBy : l.expectedOn) ?? '9999-12-31';

/**
 * Each book once, in the section of its most urgent loan (Kristi, Oct 31: as the alert line; R2 L4,
 * Nov 2: Check & update takes all five needs-you states).
 * Books not on a shelf you have here (a Reference book with a hold) come from `extra`.
 */
export function librarySections(shelves: Shelves, loans: LoanFact[], today: string, extra: ShelfBook[] = []): LibrarySections {
  const byId = new Map<string, ShelfBook>();
  for (const list of [...Object.values(shelves), extra]) for (const b of list) if (!byId.has(b.id)) byId.set(b.id, b);
  const perBook = new Map<string, LoanFact[]>();
  for (const l of loans) perBook.set(l.bookId, [...(perBook.get(l.bookId) ?? []), l]);
  const out: LibrarySections = { check: [], loan: [], hold: [], quiet: [] };
  for (const [id, mine] of perBook) {
    const book = byId.get(id);
    if (!book) continue;
    const reading = book.status === 'currently_reading';
    const ranked = [...mine].sort((a, b) => stateRank(whyOf(a, reading, today)) - stateRank(whyOf(b, reading, today)) || dateOf(a).localeCompare(dateOf(b)));
    const why = whyOf(ranked[0]!, reading, today);
    const row = { book, why, reading, loans: ranked };
    if (needsYou(why)) out.check.push(row);
    else if (why === 'on_loan') out.loan.push(row);
    else if (ranked.some((l) => quietedHold(l, today))) out.quiet.push(row);
    else out.hold.push(row);
  }
  const order = (a: LibRow, b: LibRow) =>
    stateRank(a.why) - stateRank(b.why) || dateOf(a.loans[0]!).localeCompare(dateOf(b.loans[0]!)) || a.book.title.localeCompare(b.book.title);
  out.check.sort(order);
  out.loan.sort(order);
  const back = (r: LibRow) => r.loans.find((l) => l.quietUntil)?.quietUntil ?? '9999-12-31';
  out.quiet.sort((a, b) => back(a).localeCompare(back(b)) || a.book.title.localeCompare(b.book.title));
  // Holds with no estimate go last, then by when they were placed.
  out.hold.sort((a, b) => order(a, b) || (a.loans[0]!.holdPlacedOn ?? '').localeCompare(b.loans[0]!.holdPlacedOn ?? ''));
  return out;
}

export interface HoldsToPlace {
  /** Up Next books with no copy of your own and no hold or loan, in your Up Next order. */
  upNext: ShelfBook[];
  /** The same on Future reads (folded): highest Interest first, then your order. */
  future: ShelfBook[];
  /** Books you marked Not from the library (folded), each with Suggest again. */
  notFrom: ShelfBook[];
}

/** Holds to place (Kristi, Oct 31: Up Next and Future reads; Not from the library in its own fold). */
export function holdsToPlace(shelves: Pick<Shelves, 'up_next' | 'future_reads'>, copies: CopyFact[], loans: Pick<LoanFact, 'bookId'>[]): HoldsToPlace {
  const owned = new Set(copies.filter((c) => c.how !== 'library_loan' && c.state === 'have_it').map((c) => c.bookId));
  const held = new Set(loans.map((l) => l.bookId));
  const open = (b: ShelfBook) => !owned.has(b.id) && !held.has(b.id);
  const upNext = shelves.up_next.filter((b) => open(b) && !b.notFromLibrary);
  const future = shelves.future_reads
    .filter((b) => open(b) && !b.notFromLibrary)
    .sort((a, b) => (b.interest ?? 0) - (a.interest ?? 0) || (a.position ?? Infinity) - (b.position ?? Infinity));
  const notFrom = [...shelves.up_next, ...shelves.future_reads].filter((b) => open(b) && b.notFromLibrary);
  return { upNext, future, notFrom };
}

export type ComingKind = 'borrow_by' | 'check_libby' | 'expected' | 'renew_from' | 'due';
export const COMING_WORDS: Record<ComingKind, string> = {
  borrow_by: 'borrow by',
  check_libby: 'check Libby',
  expected: 'expected',
  renew_from: 'renew from',
  due: 'due back',
};
const COMING_ORDER: ComingKind[] = ['borrow_by', 'due', 'renew_from', 'check_libby', 'expected'];

export interface ComingUp {
  on: string;
  kind: ComingKind;
  titles: string[];
}

/**
 * Coming up (the next 30 days, Oct 20): the dates that need you, in order: borrow by, due back,
 * renew from, check Libby, expected. Dates already here are in Check & update instead, so only
 * days after today count, except a due date today.
 */
export function comingUp(rows: LibRow[], today: string, days = 30): ComingUp[] {
  const until = addDays(today, days);
  const found = new Map<string, ComingUp>();
  const add = (on: string | null, kind: ComingKind, title: string, fromToday = false) => {
    if (!on || on > until || (fromToday ? on < today : on <= today)) return;
    const key = `${on}|${kind}`;
    const e = found.get(key) ?? { on, kind, titles: [] };
    if (!e.titles.includes(title)) e.titles.push(title);
    found.set(key, e);
  };
  for (const r of rows)
    for (const l of r.loans) {
      if (l.step === 'ready') add(l.borrowBy, 'borrow_by', r.book.title, true);
      if (l.step === 'borrowed' && l.dueOn) {
        add(l.dueOn, 'due', r.book.title, true);
        add(addDays(l.dueOn, -l.renewDays), 'renew_from', r.book.title);
      }
      if (l.step === 'on_hold' && l.expectedOn) {
        add(addDays(l.expectedOn, -l.checkDays), 'check_libby', r.book.title);
        add(l.expectedOn, 'expected', r.book.title);
      }
    }
  return [...found.values()].sort((a, b) => a.on.localeCompare(b.on) || COMING_ORDER.indexOf(a.kind) - COMING_ORDER.indexOf(b.kind));
}

/**
 * What a row says about one loan (R2, L5): the copy, then the state's words as on a card pill, then
 * for a hold its place in line and when it was placed: "Kindle · Due Nov 4", "Libby audio · Not
 * started · due Nov 19", "Kindle · On hold · expected Jan 13, 2027 · #3 in line · 2 copies · placed Oct 6".
 */
export function loanLine(l: LoanFact, reading: boolean, today: string): string {
  const state = whyOf(l, reading, today);
  const parts = [loanFormat(l), stateWords(state, l, today)];
  const when = whenWords(state, l, today);
  if (when) parts.push(when);
  if (l.step === 'on_hold') {
    const line = lineWords(l.placeInLine, l.libraryCopies);
    if (line) parts.push(line);
    if (l.holdPlacedOn) parts.push(`placed ${dayText(l.holdPlacedOn, today)}`);
    if (!l.expectedOn) parts.push('no estimate yet');
  }
  return parts.join(' · ');
}

/** Libby's estimate when you place a hold (Oct 20): about 2 weeks · a month · 2 months · 3+ months, or a date. */
export const ESTIMATES: { key: string; label: string; days: number | null }[] = [
  { key: '14', label: 'About 2 weeks', days: 14 },
  { key: '30', label: 'About a month', days: 30 },
  { key: '60', label: 'About 2 months', days: 60 },
  { key: '90', label: '3 months or more', days: 90 },
  { key: 'date', label: 'A date…', days: null },
  { key: 'none', label: 'No estimate', days: null },
];

/** The formats a library hold can be (as Add a copy's Library loan). */
export const HOLD_FORMATS: { key: string; format: 'kindle' | 'libby'; listen: boolean; label: string }[] = [
  { key: 'libby_kindle', format: 'kindle', listen: false, label: 'Kindle (Libby)' },
  { key: 'libby_audio', format: 'libby', listen: true, label: 'Libby audio' },
  { key: 'libby_ebook', format: 'libby', listen: false, label: 'Libby ebook' },
];

/** Libby's search for a book at your library (or Libby's shelf when no library is set). */
export function libbySearch(code: string | null, title: string, author: string): string {
  const q = encodeURIComponent(`${title} ${author}`.trim());
  return code ? `https://libbyapp.com/search/${code}/search/query-${q}/page-1` : 'https://libbyapp.com/shelf';
}

/**
 * What needs you on the Library copies tab (L2): each book once, under its most urgent state, the
 * same parts as Shelves' alert line. Its sentence is the tab dot's tooltip ("Needs you: …") and the
 * line under the description; empty when nothing needs you (no dot, no line).
 */
export function libraryNeeds(loans: LoanFact[], readingIds: Set<string>, today: string): SummaryPart[] {
  const perBook = new Map<string, LoanFact[]>();
  for (const l of loans) perBook.set(l.bookId, [...(perBook.get(l.bookId) ?? []), l]);
  return needsParts([...perBook].map(([id, mine]) => bookState(mine, readingIds.has(id), today)));
}
